<template>
  <div>
    <div class="d-flex justify-content-between">
      <PayerHeader
        v-bind="payer"
        class="px-2"
      />
      <!-- <BillingScopeDropdown
        :selected.sync="billingScope"
        single
      /> -->
    </div>
    <hr>
    <div
      class="d-flex justify-content-between align-items-center"
    >
      <h5 class="payer-section mb-0">
        {{ $t('bill.changeHistory') }}
      </h5>
      <div @click="$emit('close')">
        <i class="fas fa-times secondary-icon" />
      </div>
    </div>
    <hr class="mt-2">
    <div class="change-log">
      <div
        v-for="l in logWithProperty"
        :key="`${l.timestamp}_${l.propertyCode}`"
        class="d-flex align-items-center log-row"
      >
        <div
          style="width: 120px; min-width: 120px;"
          class="text-secondary"
        >
          {{ formatDate(l.timestamp) }}
        </div>
        <div
          style="width: 120px"
          class="d-flex align-items-center"
        >
          <i
            v-if="l.icon"
            :class="l.icon"
            class="pr-1 property-icon"
          />
          {{ l.label }}
        </div>
        <div class="d-flex align-items-center flex-grow-1">
          <div class="value-box">
            {{ l.before }}
          </div>
          <div class="px-2">
            <i class="far fa-arrow-right" />
          </div>
          <div class="value-box">
            {{ l.after }}
          </div>
        </div>
        <div
          v-if="l.user"
          v-tippy
          :content="l.user.email"
          class="text-secondary ml-2"
        >
          <i class="fa-solid fa-user" />
        </div>
      </div>

      <div class="log-row">
        <div class="text-secondary">
          <div>
            ...
          </div>
          {{ formatDateSimple(from) }}
        </div>
      </div>
      <hr>
      <div class="text-center mb-3">
        <button
          class="btn btn-sm btn-light"
          :disabled="pending"
          style="min-width: 200px"
          @click="getMore"
        >
          <Loader
            v-if="pending"
            size="20px"
            color="#333"
          />
          <span v-else>
            <i class="fas fa-arrow-down-to-dotted-line pr-2" />
            {{ $t('general.showMore') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import PayerHeader from './PayerHeader';

// Pseudo property codes written by the Payers service (PayerChangeLogCodes)
const systemCodes = {
  '#status': { label: 'payers.changeLog.status', icon: 'fas fa-box-archive' },
  '#first_name': { label: 'payers.changeLog.firstName', icon: 'fas fa-user' },
  '#last_name': { label: 'payers.changeLog.lastName', icon: 'fas fa-user' },
  '#code': { label: 'payers.changeLog.code', icon: 'fas fa-hashtag' },
  '#name': { label: 'payers.changeLog.name', icon: 'fas fa-building' },
  '#vat_number': { label: 'payers.changeLog.vatNumber', icon: 'fas fa-building' },
  '#address': { label: 'payers.changeLog.address', icon: 'fas fa-location-dot' },
  '#groups': { label: 'payers.changeLog.groups', icon: 'fas fa-users' },
};

export default {
  props: {
    payer: Object,
  },
  data: () => ({
    from: moment().subtract(90, 'days').startOf('day').format(),
    to: moment().add(1, 'day').startOf('day').format(),
    pending: false,
    response: {
      log: [],
      users: [],
    },
  }),
  components: {
    PayerHeader,
  },
  computed: {
    ...mapGetters([
      'properties',
      'payersPropertyTypes',
      'groups',
    ]),
    logWithProperty() {
      return this.response.log.map((x) => {
        const systemCode = systemCodes[x.propertyCode];
        const property = systemCode
          ? null
          : this.properties.find((p) => p.code === x.propertyCode);

        return {
          ...x,
          user: this.response.users.find((u) => u.id === x.requesterId),
          label: systemCode ? this.$t(systemCode.label) : property?.name,
          icon: systemCode ? systemCode.icon : property && this.propertyIcon(property.type),
          before: this.formatValue(x.propertyCode, x.valueBefore),
          after: this.formatValue(x.propertyCode, x.valueAfter),
        };
      });
    },
  },
  watch: {
    from() {
      this.request();
    },
  },
  methods: {
    ...mapActions(['getPayerPropertiesChangeLog']),
    formatDate(date) {
      return moment(date).format('HH:mm DD MMM YYYY');
    },
    formatDateSimple(date) {
      return moment(date).format('DD MMM YYYY');
    },
    getMore() {
      this.to = this.from;
      this.from = moment(this.from).subtract(90, 'days').startOf('day').format();
    },
    formatValue(code, value) {
      if (code === '#status') {
        return value ? this.$t(`payers.changeLog.statuses.${value}`) : value;
      }
      if (code === '#groups') {
        return this.formatGroups(value);
      }
      return value;
    },
    formatGroups(value) {
      let codes;
      try {
        codes = JSON.parse(value || '[]');
      } catch {
        return value;
      }
      if (!Array.isArray(codes) || codes.length === 0) {
        return '-';
      }
      // Payer is also assigned to all parents - show only the most specific groups
      return codes
        .filter((c) => !codes.some((other) => other.startsWith(`${c}/`)))
        .map((c) => this.groups.find((g) => g.code === c)?.path || c)
        .join(', ');
    },
    propertyIcon(type) {
      return this.payersPropertyTypes.find((x) => x.key === type)?.icon;
    },
    request() {
      this.pending = true;
      this.getPayerPropertiesChangeLog({
        params: {
          payerId: this.payer.id,
          query: {
            from: this.from,
            to: this.to,
          },
        },
      })
        .then(({ data }) => {
          this.response.log = [...this.response.log, ...data.log];
          this.response.users = [...this.response.users, ...data.users];
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
  created() {
    this.request();
  },
};
</script>

<style lang="scss" scoped>
  @import "~@/components/audit.scss";

  .payer-section {
    font-size: 16px;
    font-weight: 600 !important;
  }

  .property-icon {
    font-size: 11px;
  }

  .value-box {
    background-color: rgba($blue, 0.05);
    border-radius: 4px;
    padding: 1px 8px;
  }
</style>
